/* ==============================================================================
   Prévia e aceite de convite
   ==============================================================================
   O que a pessoa vê depois de clicar num link de convite: o nome do servidor,
   quantos membros tem, quem convidou, e dois botões.

   Por que existe uma PRÉVIA e não aceite direto
   =============================================
   Seria mais curto entrar no servidor no clique. Mas um link de convite é
   compartilhável e opaco: `/convite/tZ8UAX5gSm0f` não diz para onde leva. Entrar
   direto significa que clicar num link de terceiro adiciona a pessoa a um
   servidor que ela não escolheu, com o nome dela visível para todos os membros
   dele — inclusive para quem ela bloqueou.

   A prévia é o consentimento. É por isso que `GET /invites/:code` não exige
   autenticação e não conta uso.

   Por que a contagem de usos aparece
   ==================================
   Um convite com "3 de 5 usos" comunica que o link é limitado e pode expirar —
   o que muda a decisão de esperar ou entrar agora. Esconder isso faz um convite
   esgotado parecer um erro do app.
   ============================================================================== */

import { useEffect, useState } from 'react';

import type { ConcordApi } from '../lib/concord.ts';

interface InvitePreview {
  code: string;
  guild: {
    id: string;
    name: string;
    description: string | null;
    memberCount: number;
    iconUrl?: string | null;
  };
  inviter: { id: string; username: string; displayName: string | null } | null;
  channelName: string | null;
  expiresAt: string | null;
  maxUses: number | null;
  uses: number;
  grantRoleNames?: string[];
  alreadyMember?: boolean;
}

export interface InviteScreenProps {
  api: ConcordApi;
  code: string;
  /** Chamado com o id da guild após entrar (ou se já era membro). */
  onJoined: (guildId: string) => void;
  onDismiss: () => void;
}

export function InviteScreen({ api, code, onJoined, onDismiss }: InviteScreenProps) {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const result = (await api.previewInvite(code)) as unknown as InvitePreview;
        if (active) setPreview(result);
      } catch (cause) {
        if (!active) return;
        /*
         * A mensagem do servidor é preferida à genérica.
         *
         * "Este convite expirou", "Este convite atingiu o limite de usos" e
         * "Convite não encontrado" são situações diferentes com ações
         * diferentes — pedir outro link, esperar, ou verificar se copiou certo.
         * Uma mensagem única para as três não ajuda em nenhuma.
         */
        setError(
          cause instanceof Error && cause.message.length > 0
            ? cause.message
            : 'Não foi possível carregar este convite.',
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [api, code]);

  const accept = async () => {
    if (preview === null || joining) return;
    setJoining(true);
    setError(null);
    try {
      const result = await api.acceptInvite(code);
      onJoined(
        (result as unknown as { guildId?: string }).guildId ?? preview.guild.id,
      );
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message.length > 0
          ? cause.message
          : 'Não foi possível entrar neste servidor.',
      );
      setJoining(false);
    }
  };

  return (
    <div
      className="grid h-full place-items-center px-6"
      style={{ background: 'var(--color-surface-sunken)' }}
    >
      <div
        className="w-full max-w-sm overflow-hidden text-center"
        style={{
          borderRadius: 'var(--radius-xl)',
          background: 'var(--color-surface-raised)',
          boxShadow: 'var(--glass-edge), 0 24px 64px oklch(0% 0 0 / 0.45)',
        }}
      >
        {/* Banda de acento no topo: dá identidade sem depender de o servidor
            ter banner. */}
        <div
          aria-hidden="true"
          style={{ height: 72, background: 'var(--ambient-glow), var(--color-accent-subtle)' }}
        />

        <div className="px-6 pb-6" style={{ marginTop: -28 }}>
          {/* ---- Ícone / iniciais ---- */}
          <div
            className="mx-auto grid place-items-center overflow-hidden"
            style={{
              width: 56,
              height: 56,
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-lg)',
              fontWeight: 700,
              boxShadow: '0 0 0 3px var(--color-surface-raised), var(--glow-medium)',
            }}
          >
            {preview?.guild.iconUrl != null ? (
              <img
                src={preview.guild.iconUrl}
                alt=""
                className="size-full object-cover"
                /*
                 * `loading="eager"` aqui, e é o oposto do padrão que uso.
                 *
                 * Esta imagem é o conteúdo principal da tela e está acima da
                 * dobra. `lazy` num elemento visível de imediato só adiciona um
                 * quadro de atraso.
                 */
                loading="eager"
              />
            ) : (
              initials(preview?.guild.name ?? '?')
            )}
          </div>

          {error !== null ? (
            <>
              <h1
                className="mt-4 font-bold"
                style={{
                  fontSize: 'var(--text-lg)',
                  color: 'var(--color-text-primary)',
                  letterSpacing: 'var(--tracking-tight)',
                }}
              >
                Convite indisponível
              </h1>
              <p
                className="mt-2"
                style={{
                  fontSize: 'var(--text-sm)',
                  lineHeight: 'var(--leading-relaxed)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {error}
              </p>
              <button
                type="button"
                onClick={onDismiss}
                className="mt-5 w-full px-4 py-2.5 font-semibold"
                style={{
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-surface-overlay)',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                Continuar sem entrar
              </button>
            </>
          ) : preview === null ? (
            <p
              className="mt-6 mb-2"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-disabled)' }}
            >
              Carregando convite…
            </p>
          ) : (
            <>
              <p
                className="mt-4"
                style={{
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {preview.alreadyMember === true
                  ? 'Você já é membro de'
                  : 'Você foi convidado para'}
              </p>

              <h1
                className="mt-1 font-bold"
                style={{
                  fontSize: 'var(--text-xl)',
                  color: 'var(--color-text-primary)',
                  letterSpacing: 'var(--tracking-tight)',
                }}
              >
                {preview.guild.name}
              </h1>

              {preview.guild.description !== null && (
                <p
                  className="mt-1.5"
                  style={{
                    fontSize: 'var(--text-xs)',
                    lineHeight: 'var(--leading-relaxed)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {preview.guild.description}
                </p>
              )}

              {/* ---- Fatos do convite ---- */}
              <dl
                className="mt-4 flex items-center justify-center gap-4"
                style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
              >
                <div>
                  <dt className="sr-only">Membros</dt>
                  <dd>
                    <strong style={{ color: 'var(--color-text-secondary)' }}>
                      {preview.guild.memberCount}
                    </strong>{' '}
                    {preview.guild.memberCount === 1 ? 'membro' : 'membros'}
                  </dd>
                </div>
                {preview.maxUses !== null && (
                  <div>
                    <dt className="sr-only">Usos</dt>
                    <dd>
                      <strong style={{ color: 'var(--color-text-secondary)' }}>
                        {preview.uses}
                      </strong>
                      {' de '}
                      {preview.maxUses} usos
                    </dd>
                  </div>
                )}
              </dl>

              {preview.inviter !== null && (
                <p
                  className="mt-3"
                  style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
                >
                  Convidado por{' '}
                  <strong style={{ color: 'var(--color-text-secondary)' }}>
                    {preview.inviter.displayName ?? preview.inviter.username}
                  </strong>
                </p>
              )}

              {/*
               * Cargos concedidos aparecem ANTES de aceitar.
               *
               * Um convite pode dar cargo automaticamente. Descobrir isso depois
               * de entrar é descobrir que o link tinha uma consequência que não
               * estava escrita — e num servidor grande, receber um cargo pode
               * significar acesso a canais que a pessoa não esperava ver.
               */}
              {preview.grantRoleNames !== undefined &&
                preview.grantRoleNames.length > 0 && (
                  <p
                    className="mt-3"
                    style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
                  >
                    Este convite concede:{' '}
                    <strong style={{ color: 'var(--color-accent-on-subtle)' }}>
                      {preview.grantRoleNames.join(', ')}
                    </strong>
                  </p>
                )}

              <div className="mt-5 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => void accept()}
                  disabled={joining}
                  className="w-full px-4 py-2.5 font-semibold"
                  style={{
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-accent-solid)',
                    color: 'var(--color-on-solid)',
                    fontSize: 'var(--text-sm)',
                    opacity: joining ? 0.6 : 1,
                    boxShadow: 'var(--glow-medium)',
                  }}
                >
                  {joining
                    ? 'Entrando…'
                    : preview.alreadyMember === true
                      ? 'Abrir servidor'
                      : 'Aceitar convite'}
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="w-full px-4 py-2"
                  style={{
                    borderRadius: 'var(--radius-full)',
                    color: 'var(--color-text-tertiary)',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  Agora não
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Até duas letras, para o quadrado de fallback. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`.toUpperCase();
}
